import { Alert, Box, Snackbar, Stack, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import EmailIcon from '../../../icons/EmailIcon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { getUpdatingOwner, selectOwnerDetails, selectOwnersState } from '../sagas/ownersSlice';
import PhoneIcon from '../../../icons/PhoneIcon.svg';
import RemoveVendorIcon from '../../../icons/RemovePropertyIcon.svg';
import ReinstateVendorIcon from '../../../icons/re-instate.svg';
import OwnerStatusChangeModal from './OwnerStatusChangeModal';
import { useEffect, useState } from 'react';
import { UpdateOwnerDto, UpdateOwnerStatusEnum } from '@inosio/payments-sdk';

const useStyles = makeStyles((theme) => ({
  labelText: {
    fontFamily: 'Open Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '700 !important',
    fontSize: '18px !important',
    lineHeight: '25px !important',
    color: '#14142B',
  },
}));

export interface OwnerDetailsGeneralInfoProps {
  ownerUuid: string;
}
export function OwnerDetailsGeneralInfo(props: OwnerDetailsGeneralInfoProps) {
  const classes = useStyles();
  const dispatch = useDispatch();

  //selectors
  const ownerDetails = useSelector(selectOwnerDetails);
  const { isUpdatingOwner, isUpdatingOwnerSuccess, isUpdatingOwnerFailed } = useSelector(selectOwnersState);

  const { FirstName, LastName, IntegrationEmail, PhoneNumber, DeactivatedDate, ReactivatedDate } = ownerDetails;
  const statusChangeAction = !!ReactivatedDate || !DeactivatedDate ? UpdateOwnerStatusEnum.Deactivate : UpdateOwnerStatusEnum.Reactivate;

  //local states
  const [openStatusChangeModal, setOpenStatusChangeModal] = useState(false);
  const [updateOwnerErrorMessage, setUpdateOwnerErrorMessage] = useState<string | null>(null);
  const [openUpdateOwnerSuccessAlert, setOpenUpdateOwnerSuccessAlert] = useState(false);

  //handlers
  const handleStatusChange = () => {
    dispatch({
      type: getUpdatingOwner.type,
      payload: {
        ownerUuid: props.ownerUuid,
        updateOwnerDto: {
          Status: statusChangeAction,
        } as UpdateOwnerDto,
      },
    });
  };

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenUpdateOwnerSuccessAlert(false);
  };

  //useEffects
  useEffect(() => {
    if (isUpdatingOwnerSuccess) {
      setUpdateOwnerErrorMessage(null);
      setOpenStatusChangeModal(false);
      setOpenUpdateOwnerSuccessAlert(true);
    } else if (isUpdatingOwnerFailed) {
      setUpdateOwnerErrorMessage('Error updating status of the owner. Please try again later or contact support');
    }
  }, [isUpdatingOwnerSuccess, isUpdatingOwnerFailed]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ width: '100%', p: 1, borderBottom: '1px solid #EEEEEE', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ ml: 2, color: '#14142B' }} className={classes.labelText}>
          {`Owner Details`}
        </Typography>
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
          <img
            onClick={() => {
              setOpenStatusChangeModal(true);
            }}
            src={statusChangeAction === UpdateOwnerStatusEnum.Deactivate ? RemoveVendorIcon : ReinstateVendorIcon}
            alt="Remove Owner"
            style={{ cursor: 'pointer' }}
          />
        </Stack>
      </Box>

      <Box sx={{ ml: 3 }}>
        <Typography sx={{ mt: 4, color: '#14142B', fontSize: '45px', fontFamily: 'Work Sans' }}>
          {FirstName} {LastName}
        </Typography>

        <Box sx={{ display: 'flex', mt: 4, alignItems: 'center' }}>
          <img src={PhoneIcon} alt="phone icon" />
          <Typography sx={{ ml: 3, color: '#14142B' }} className={classes.labelText}>
            {PhoneNumber}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', mt: 3, alignItems: 'center' }}>
          <img src={EmailIcon} alt="email icon" />
          <Typography sx={{ ml: 3, color: '#14142B' }} className={classes.labelText}>
            {IntegrationEmail}
          </Typography>
        </Box>
      </Box>

      {openStatusChangeModal && (
        <OwnerStatusChangeModal
          open={openStatusChangeModal}
          closeCallback={() => {
            setOpenStatusChangeModal(false);
          }}
          confirmCallback={handleStatusChange}
          status={statusChangeAction}
          isChangingStatus={isUpdatingOwner}
          errorMessage={updateOwnerErrorMessage}
        />
      )}

      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={openUpdateOwnerSuccessAlert} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="success">
          Owner updated successfully.
        </Alert>
      </Snackbar>
    </Box>
  );
}
